import { FamilyDto } from 'api/families/models/FamilyDto';

function removeAccents (text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export function cleanElementName (value: string): string {
    if (!value) return value;

    // Remove price suffix like "- 0,89€/Kg"
    const cleaned = value.replace(/\s*[-–—]\s*\d+[.,]?\d*\s*€.*$/g, '').trim();
    if (cleaned !== value.trim()) return cleaned;

    // Fallback: cut at " - " for family matching
    const dashIndex = value.indexOf(' - ');
    if (dashIndex > 0) {
        return value.substring(0, dashIndex).trim();
    }

    return value.trim();
}

export interface FamilyMatch {
    familyId: string;
    subFamilyId: string | null;
}

export function matchFamily (elementName: string, families: FamilyDto[]): FamilyMatch | null {
    if (!elementName) return null;

    const normalizedElementName = removeAccents(elementName.toLowerCase());

    // First, match parent family by checking if element name STARTS with the family name
    const sortedParents = [...families]
        .filter(f => !f.parentId && f.name)
        .sort((a, b) => b.name.length - a.name.length);

    let matchedFamily: FamilyDto | null = null;
    for (const family of sortedParents) {
        const normalizedName = removeAccents(family.name.toLowerCase());
        if (normalizedElementName.startsWith(normalizedName)) {
            matchedFamily = family;
            break;
        }
    }

    if (!matchedFamily) {
        // Fallback: try matching by subfamily name
        const allSubs: Array<{ sub: FamilyDto; parentId: string }> = [];
        for (const family of families) {
            if (family.subFamilies) {
                for (const sub of family.subFamilies) {
                    if (sub.name) allSubs.push({ sub, parentId: family.id });
                }
            }
        }
        const sortedSubs = allSubs.sort((a, b) => b.sub.name.length - a.sub.name.length);
        for (const { sub, parentId } of sortedSubs) {
            const normalizedSubName = removeAccents(sub.name.toLowerCase());
            if (normalizedElementName.startsWith(normalizedSubName)) {
                return { familyId: parentId, subFamilyId: sub.id };
            }
        }
        return null;
    }

    // Then, try to match a subfamily within the matched family
    if (matchedFamily.subFamilies && matchedFamily.subFamilies.length > 0) {
        const sortedSubs = [...matchedFamily.subFamilies]
            .filter(f => f.name)
            .sort((a, b) => b.name.length - a.name.length);

        for (const sub of sortedSubs) {
            const normalizedSubName = removeAccents(sub.name.toLowerCase());
            if (normalizedElementName.includes(normalizedSubName)) {
                return {
                    familyId: matchedFamily.id,
                    subFamilyId: sub.id,
                };
            }
        }
    }

    return {
        familyId: matchedFamily.id,
        subFamilyId: null,
    };
}

/**
 * Resolves a family (and optionally subfamily) by matching the exact name written in an Excel
 * column. Comparison is case- and accent-insensitive. Returns null when the family name does not
 * match any parent family, leaving the row unmatched for manual selection.
 */
export function matchFamilyByName (familyName: string | null, subFamilyName: string | null, families: FamilyDto[]): FamilyMatch | null {
    const normalizedFamilyName = familyName ? removeAccents(familyName.toLowerCase()).trim() : '';
    if (!normalizedFamilyName) return null;

    const matchedFamily = families.find(f =>
        !f.parentId && f.name && removeAccents(f.name.toLowerCase()).trim() === normalizedFamilyName
    );
    if (!matchedFamily) return null;

    const normalizedSubFamilyName = subFamilyName ? removeAccents(subFamilyName.toLowerCase()).trim() : '';
    if (normalizedSubFamilyName && matchedFamily.subFamilies) {
        const matchedSub = matchedFamily.subFamilies.find(sf =>
            sf.name && removeAccents(sf.name.toLowerCase()).trim() === normalizedSubFamilyName
        );
        if (matchedSub) {
            return { familyId: matchedFamily.id, subFamilyId: matchedSub.id };
        }
    }

    return { familyId: matchedFamily.id, subFamilyId: null };
}
